import { isGlobalSurface } from '@/lib/surface-mode';
import type { MarketHub } from './market-hubs.types';

export type { MarketHub };

export interface MarketHubs {
  /** The market whose Hub is serving this page. */
  current: MarketHub;
  /** Every other market Hub in this build's environment, sorted by name. */
  others: MarketHub[];
}

/**
 * A production market answers on both its apex host and the pre-apex-migration `next` host
 * (hub.intrum.co.uk and hub.next.intrum.co.uk serve one build), so the two compare equal.
 *
 * Known collision: the production global Hub is hub.intrum.com and INTL is hub.next.intrum.com, which
 * this rule makes equal. The global Hub never reaches this code (getMarketHubs returns null on global
 * surface builds), and INTL cannot take the apex host while the global Hub holds it.
 */
function canonicalOrigin(origin: string): string {
  return origin.replace('.next.', '.');
}

/**
 * Resolve the market behind `origin` from the baked list. Returns null when the origin is not one of
 * them (local dev, the global Hub, a build whose redirect URI matched no catalog), which hides the switcher.
 */
export function resolveMarketHubs(origin: string, markets: MarketHub[]): MarketHubs | null {
  const wanted = canonicalOrigin(origin);
  const current = markets.find((market) => canonicalOrigin(market.hubOrigin) === wanted);
  if (!current) return null;
  const others = markets.filter((market) => market !== current).sort((a, b) => a.name.localeCompare(b.name));
  return { current, others };
}

/** The global Hub serves every market behind one origin and uses the AuthKit org picker instead. */
export function getMarketHubs(): MarketHubs | null {
  if (isGlobalSurface()) return null;
  return resolveMarketHubs(window.location.origin, __MARKET_HUBS__);
}

export function marketLoginUrl(market: MarketHub): string {
  return `${market.hubOrigin}/login`;
}

/** Short market label from the catalog code (`INTRUM_INTL` → `INTL`), which unlike the headquarters country fits the international market. */
export function marketShortCode(market: MarketHub): string {
  return market.code.slice(market.code.lastIndexOf('_') + 1);
}
